import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import { ErrorState, LoadingState } from '@/components/admin';
import { useClient } from '@/features/clients/hooks';
import { CaseReportEditor } from '@/features/sessions/components/CaseReportEditor';
import { useCaseReports, useSession } from '@/features/sessions/hooks';
import { requirePermission } from '@/lib/guards';
import { surfacePaths } from '@/lib/paths';
import { useAuthStore } from '@/stores/authStore';

export const Route = createFileRoute('/app/sessions/$sessionId/case-reports/$reportId')({
  beforeLoad: () => {
    requirePermission('case_report:read');
  },
  component: ClinicalCaseReportScreen,
  // Editor page owns the full canvas — no shell padding around the card.
  staticData: { fullBleed: true },
});

export function ClinicalCaseReportScreen() {
  const { sessionId, reportId } = useParams({
    from: '/app/sessions/$sessionId/case-reports/$reportId',
  });
  const navigate = useNavigate();
  const sessionQ = useSession(sessionId);
  const reportsQuery = useCaseReports(sessionId);
  const clientQ = useClient(sessionQ.data?.clientId ?? undefined);
  const userFirstName = useAuthStore((s) => s.user?.firstName ?? 'you');
  const paths = surfacePaths('clinical');

  if (sessionQ.isLoading || reportsQuery.isLoading) return <LoadingState variant="form" />;
  if (sessionQ.error || reportsQuery.error) {
    const err = reportsQuery.error ?? sessionQ.error;
    return (
      <ErrorState
        description={err instanceof Error ? err.message : undefined}
        onRetry={() => {
          void reportsQuery.refetch();
          void sessionQ.refetch();
        }}
      />
    );
  }
  if (sessionQ.data === undefined) return <p className="p-6">Session not found.</p>;

  const report = reportsQuery.data?.find((r) => r.id === reportId);
  if (!report) {
    return <ErrorState title="Case report not found" />;
  }

  const session = sessionQ.data;
  const client = clientQ.data;
  const clientName =
    client !== undefined
      ? `${client.firstName} ${client.lastName}`
      : (session.clientName ?? 'Client');

  const returnToSession = () => {
    void navigate({ to: paths.sessions.detail(sessionId) });
  };

  // After a reviewer's approve / reject the report leaves their queue:
  //   - lead approves → BE flips reviewerId to chief; lead's listReports
  //     filters the report out (no longer reviewer, not author) → re-render
  //     of this screen would 404 with "Case report not found".
  //   - reject → status 'rejected', also drops off the lead's listReports.
  //   - chief approves → final, status 'approved'; chief still sees it
  //     (they read all at location), but the queue is the more useful
  //     landing page after acting.
  // Navigate back to the queue in all cases; toast on the editor confirms
  // what just happened.
  const returnToReviewQueue = () => {
    void navigate({ to: '/app/case-reports/review-queue' });
  };

  // Full-bleed by spec — D10 dropped JournalPageHeader + StatusTimeline
  // wrapper. The new ClinicalEditorShell paints its own sticky topbar
  // (kind label, status pill, back link), footer chrome, and a side panel
  // hosting the status strip + reviewer card.
  return (
    <CaseReportEditor
      sessionId={sessionId}
      practitionerId={session.practitionerId}
      clientId={session.clientId}
      clientName={clientName}
      scheduledStart={session.scheduledStart}
      authorName={userFirstName}
      report={report}
      onSubmitted={returnToSession}
      onReviewed={returnToReviewQueue}
    />
  );
}
